import { registerSW } from 'virtual:pwa-register';
import { TokenStorage } from '@/api/interceptors';

// Types
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

type UpdateCallback = (updateAvailable: boolean) => void;
type InstallPromptCallback = (event: BeforeInstallPromptEvent | null) => void;

// State
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
let updateCallbacks: UpdateCallback[] = [];
let installCallbacks: InstallPromptCallback[] = [];
let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;

/**
 * Register the PWA service worker.
 * Call once on app startup.
 */
export function registerPWA(): void {
  updateSW = registerSW({
    onNeedRefresh() {
      updateCallbacks.forEach((cb) => cb(true));
    },
    onOfflineReady() {
      console.log('[PWA] App is ready for offline use');
    },
    onRegisteredSW(swUrl, registration) {
      console.log('[PWA] Service worker registered:', swUrl);
      if (registration) {
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('[PWA] Service worker registration error:', error);
    },
  });
}

/**
 * Apply a waiting update: tell the new worker to take over, then let it reload the page.
 *
 * The reload is normally done by workbox-window's own `controlling` listener, which fires once the
 * new worker takes control. The timer is a backstop for the case where that event never arrives
 * (a worker stuck in `waiting`, a browser that swallows the message): without it the button just
 * looks broken. Reloading anyway is harmless — at worst the page comes back with the same banner.
 */
export async function applyUpdate(): Promise<void> {
  if (!updateSW) return;

  const reloadFallback = setTimeout(() => {
    window.location.reload();
  }, UPDATE_TAKEOVER_TIMEOUT_MS);

  navigator.serviceWorker?.addEventListener(
    'controllerchange',
    () => clearTimeout(reloadFallback),
    { once: true }
  );

  await updateSW(true);
}

/** How long to wait for the new worker to take control before reloading anyway. */
const UPDATE_TAKEOVER_TIMEOUT_MS = 3000;

/** `screen.orientation` with the lock/unlock pair that TS's lib.dom still omits. */
type LockableOrientation = ScreenOrientation & {
  lock?: (orientation: string) => Promise<void>;
  unlock?: () => void;
};

function getOrientation(): LockableOrientation | undefined {
  return typeof screen !== 'undefined'
    ? (screen.orientation as LockableOrientation | undefined)
    : undefined;
}

/**
 * Lock screen orientation to portrait on supported devices.
 * Works in standalone PWA mode; silently fails in regular browsers.
 *
 * **No-op for the customer portal.** This is a hard lock of the *device*: in an installed
 * Android PWA it survives for the whole session and turning off the CSS `#landscape-blocker`
 * does not undo it. Portrait-only is a deliberate choice for a mechanic holding a phone
 * (`uiStore.disableLandscapeOnMobile`), and hostile for a customer on an iPad — which lands
 * exactly on the blocker's 1024px boundary. Client screens must work in both orientations
 * (vault/specs/client-portal/client-portal-pwa-and-installation.md § "Блокер ландшафта").
 *
 * The audience lives in localStorage and is therefore readable at boot, before React mounts
 * and before `/users/me` comes back — which is the only reason this check can live here at
 * all. It covers a returning customer (a stored client session). It does NOT cover a browser
 * that boots with no session, or with an internal one, and then logs in as a customer: for
 * that, `ClientLayout` calls {@link unlockOrientation} in an effect. The two are independent
 * and complementary — neither depends on the other having run.
 */
export function lockPortraitOrientation(): void {
  if (TokenStorage.getAudience() === 'client') return;

  const orientation = getOrientation();
  if (orientation?.lock) {
    orientation.lock('portrait-primary').catch(() => {
      // Orientation lock not supported in this context (e.g. browser tab)
    });
  }
}

/**
 * Release any orientation lock this session applied, letting the device rotate freely.
 *
 * The counterpart to {@link lockPortraitOrientation}, for the case its boot-time check
 * cannot see: a session that *becomes* a client session after startup. Safe to call
 * unconditionally and repeatedly — `unlock()` is absent in browsers that never locked
 * anything, and throws nothing when no lock is held.
 */
export function unlockOrientation(): void {
  const orientation = getOrientation();
  try {
    orientation?.unlock?.();
  } catch {
    // Not supported in this context (e.g. a plain browser tab) — nothing was locked.
  }
}

/**
 * Set up beforeinstallprompt event listener.
 * Call early, before React renders.
 */
export function setupInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e as BeforeInstallPromptEvent;
    installCallbacks.forEach((cb) => cb(deferredInstallPrompt));
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    installCallbacks.forEach((cb) => cb(null));
    console.log('[PWA] App installed successfully');
  });
}

export async function promptInstall(): Promise<boolean> {
  if (!deferredInstallPrompt) return false;

  await deferredInstallPrompt.prompt();
  const { outcome } = await deferredInstallPrompt.userChoice;

  if (outcome === 'accepted') {
    deferredInstallPrompt = null;
    return true;
  }
  return false;
}

export function canInstall(): boolean {
  return deferredInstallPrompt !== null;
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in window.navigator && (window.navigator as Navigator & { standalone?: boolean }).standalone === true)
  );
}

export function onUpdateAvailable(callback: UpdateCallback): () => void {
  updateCallbacks.push(callback);
  return () => {
    updateCallbacks = updateCallbacks.filter((cb) => cb !== callback);
  };
}

export function onInstallPromptAvailable(callback: InstallPromptCallback): () => void {
  installCallbacks.push(callback);
  if (deferredInstallPrompt) {
    callback(deferredInstallPrompt);
  }
  return () => {
    installCallbacks = installCallbacks.filter((cb) => cb !== callback);
  };
}
